import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import pDebounce from 'p-debounce';

import { useAuth } from 'src/hooks';

import { getActivityLog } from 'src/utils/urls';
import { pageLength } from 'src/utils/appConstants';
import { setListRequestData } from '../utils/listReqData';
import { activityStore } from '../store/activityStore';

import { ActivityLogFilterType } from '../types/filterOptionsType';
import { ActivityLogListType } from '../types/activityLogType';

async function fetchActivityLog({
  requestData,
  token,
  tokenType,
  pageParam,
}: TVariables): Promise<TPage> {
  requestData.append('start', `${pageParam}`);
  activityStore.getState().setPageParam(pageParam);
  const response = await fetch(getActivityLog, {
    method: 'POST',
    headers: {
      Authorization: `${tokenType} ${token}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: requestData,
  });
  return response.json();
}

export type TVariables = {
  requestData: any;
  token: string;
  tokenType: string;
  pageParam: number;
};

export type TError = { message: string };

export type TPage = {
  data: Array<ActivityLogListType>;
  draw: number;
  error: null;
  isAutoRefreshEnabled: boolean;
  pageNumber: number;
  recordsFiltered: number;
  recordsTotal: number;
};

const debouncedFetch = pDebounce(fetchActivityLog, 500);

function useGetActivityLog(
  filterOptions: ActivityLogFilterType,
  selectedToggle: string,
  isExternalUser: boolean,
  isActivityEmpty?: boolean,
): UseInfiniteQueryResult<TPage, unknown> {
  const { token, tokenType } = useAuth();
  return useInfiniteQuery(
    ['get_activity_logs', filterOptions],
    ({ pageParam = 0 }) => {
      const requestData = setListRequestData(
        filterOptions,
        selectedToggle,
        isExternalUser,
      );
      return debouncedFetch({ token, tokenType, requestData, pageParam });
    },
    {
      getNextPageParam: (lastPage: TPage, allPages: Array<TPage>) => {
        if (lastPage !== null && lastPage?.data?.length === pageLength)
          return allPages?.length * pageLength;
      },
      getPreviousPageParam: (firstPage: TPage, allPages: Array<TPage>) => {
        if (firstPage !== null && firstPage?.data?.length === pageLength)
          return allPages?.length - 1 * pageLength;
      },

      staleTime: 60 * 1000,
      enabled:
        (selectedToggle === 'callDocuments' || selectedToggle === '') &&
        !isActivityEmpty,
      refetchOnWindowFocus: false,
    },
  );
}

export { useGetActivityLog };
